import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import tw from 'twin.macro';
import { faLightbulb, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PercentageSelect from '../PercentageSelect';
import { useMutation } from '@apollo/client';
import { GET_CAR, UPDATE_CAR, UPDATE_CAR_DISCOUNT } from '../../services/carService/queries';
import { useBrandRules } from '../../hooks/useHostCapabilities';
import { carAgeLimitOptions } from '../../utils/brandAge';
import { getBrand } from '../../constants/brands';
import { IDiscount } from '../../../__generated__/graphql';
import { Link } from 'react-router-dom';
import { Button } from '../button';

const PricingSection = styled.div`
  ${tw`mb-8`}
`;

const SectionTitle = styled.h1`
  ${tw`text-2xl font-bold flex items-center gap-2 mb-4`}
`;

const InfoIcon = styled(FontAwesomeIcon)`
  ${tw`text-text-tertiary cursor-help`}
`;

const BoostPricingCard = styled.div`
  ${tw`border border-border-default rounded-lg p-6 mb-4`}
`;

const SubSectionTitle = styled.h2`
  ${tw`text-xl font-bold mb-4`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-4`}
`;

const LinkButton = styled.button`
  ${tw`text-black font-semibold flex items-center gap-2 mb-6`}

  &:hover {
    ${tw`text-black`}
  }
`;

const TipCard = styled.div`
  ${tw`bg-bg-secondary p-4 rounded-lg flex items-start gap-3`}
`;

const DurationDiscountSection = styled.div`
  ${tw`border border-border-default rounded-lg p-6 mb-4`}
`;

const SubTitle = styled.h3`
  ${tw`text-base font-semibold mb-2`}
`;

const DiscountDescription = styled.p`
  ${tw`text-text-secondary mb-6`}
`;

const InputGroup = styled.div`
  ${tw`
    mb-6 
    w-full 
    max-w-md
  `}
`;

const InputLabel = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-1`}
`;

const SaveButtonContainer = styled.div`
  ${tw`
    w-full
    max-w-md
    mt-6
    py-2
  `}

  &:enabled {
    ${tw`bg-black`}
  }

  &:enabled:hover {
    ${tw`bg-purple-700`}
  }

  &:disabled {
    ${tw`bg-gray-300 cursor-not-allowed`}
  }
`;
const AgeSelect = styled.select`
  ${tw`h-[44px] w-full p-2 border border-border-default rounded-lg`}
`;
// const AgeOption = styled.option`
//   ${tw`text-text-secondary`}
// `;

interface AgeLimitSectionProps {
  ageLimit: number;
  carId: string; // Add carId prop
}

const AgeLimitComponent: React.FC<AgeLimitSectionProps> = ({ ageLimit, carId }) => {
  const [ageEdge, setAgeEdge] = useState(ageLimit ?? 18);
  const { minimumAge: brandMinimumAge } = useBrandRules();
  const ageLimitOptions = carAgeLimitOptions(brandMinimumAge);

  // Add mutation hook
  const [updateCar, { loading }] = useMutation(UPDATE_CAR, {
    variables: {
      id: carId,
      updateCarInput: {
        ageLimit: ageEdge,
      },
    },
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    onCompleted: () => {
      // Handle success (e.g., show success message)
      toast.success('Limit age updated successfully');
    },
    onError: error => {
      // Handle error (e.g., show error message)
      toast.error('Error updating limit age');
    },
  });
  const handleSetLastMinBooth = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAgeEdge(Number(e.target.value));
  };

  const handleSave = () => {
    updateCar();
  };

  return (
    <PricingSection>
      <SectionTitle>
        Age limit
        <InfoIcon icon={faInfoCircle} />
      </SectionTitle>

      <BoostPricingCard>
        <SubSectionTitle>Limit age for rent this car</SubSectionTitle>
        <Description>
          <li>if you choose 18, anyone who is 18 or older can rent this car.</li>
          <li>if you choose 21, anyone who is 21 or older can rent this car.</li>
          <li>if you choose 25, anyone who is 25 or older can rent this car.</li>
        </Description>
        <InputGroup>
          <InputLabel>Age limit</InputLabel>
          <AgeSelect value={ageEdge} onChange={handleSetLastMinBooth}>
            {/* Spec 014b US-5: not below this site's minimum driver age. */}
            {ageLimitOptions.map(age => (
              <option key={age} value={age}>
                {age}
              </option>
            ))}
          </AgeSelect>
        </InputGroup>

        {brandMinimumAge !== null && brandMinimumAge > 18 && (
          <Description data-testid="brand-minimum-age-note">
            {`${getBrand().name}'s minimum driver age is ${brandMinimumAge}.`}
          </Description>
        )}
        <TipCard>
          <FontAwesomeIcon icon={faLightbulb} tw="text-black mt-1" />
          <p>
            please go to <Link to="/cars/host/miscellaneous-fees">trip fee</Link> to set Youngster
            fee
          </p>
        </TipCard>
        <SaveButtonContainer>
          <Button text="Save" onClick={handleSave} isLoading={loading} isDisabled={loading} />
        </SaveButtonContainer>
      </BoostPricingCard>
    </PricingSection>
  );
};

export default AgeLimitComponent;
